import { type MouseEvent, useState } from 'react';

import { useAppTranslations } from '@treneo/shared/i18n';
import { IconSizes, NotificationsIcon } from '@treneo/shared/icons';
import { Colors } from '@treneo/shared/themes';
import { Badge, Box, IconButton, Menu, Typography } from '@treneo/shared/ui';

import type { Notification } from '../../types/notification.types';

import { NotificationItem } from './notification-item';

type Props = Readonly<{
  notifications: readonly Notification[];
  isLoading?: boolean;
}>;

export const NotificationBell = ({ notifications }: Props) => {
  const { t } = useAppTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const count = notifications.length;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const detailsLinkLabel = t('notifications.viewDetails');
  const emptyMessage = t('notifications.empty');
  const ariaLabel = t('notifications.ariaLabel');

  const listContent =
    notifications.length === 0 ? (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography variant='body2' color='text.secondary'>
          {emptyMessage}
        </Typography>
      </Box>
    ) : (
      notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          detailsLinkLabel={detailsLinkLabel}
        />
      ))
    );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={ariaLabel}
        aria-controls={open ? 'notification-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        size='medium'
      >
        <Badge badgeContent={count} color='primary' max={99}>
          <NotificationsIcon fontSize={IconSizes.medium} />
        </Badge>
      </IconButton>
      <Menu
        id='notification-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              maxWidth: 400,
              maxHeight: 360,
              border: `2px solid ${Colors.darkGrey}`,
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(62, 66, 71, 0.12), 0 4px 12px rgba(62, 66, 71, 0.08)',
              backgroundColor: Colors.white
            }
          }
        }}
      >
        {listContent}
      </Menu>
    </>
  );
};
